import { isEmpty } from "lodash";
import { useEffect, useState } from "react";
import {
  Control,
  FieldErrors,
  FieldValues,
  UseFormGetValues,
  UseFormRegister,
  UseFormReset,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
  useFieldArray,
} from "react-hook-form";

export type ReceivedProps = {
  register: UseFormRegister<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
  getValues: UseFormGetValues<FieldValues>;
  watch: UseFormWatch<FieldValues>;
  reset: UseFormReset<FieldValues>;
  trigger: UseFormTrigger<FieldValues>;
  errors: FieldErrors<FieldValues>;
  control: Control<FieldValues>;
  isView: boolean;
};

const useWorkCost = (props: ReceivedProps) => {
  const { fields, append, remove } = useFieldArray({
    control: props.control,
    name: "work_cost_items",
  });

  const [openModalDelete, setOpenModalDelete] = useState<boolean>(false);
  const [removeIdx, setRemoveIdx] = useState<number | null>(null);
  const [textFieldValues, setTextFieldValues] = useState<{
    [key: string]: string;
  }>({});
  const [isComposing, setIsComposing] = useState<{
    [key: string]: boolean;
  }>({});

  useEffect(() => {
    if (props.reset && isEmpty(props.getValues("work_cost_items"))) {
      props.reset({
        ...props.getValues(),
        work_cost_items: [
          {
            material_cost: null,
            product_name: null,
            part_number: null,
            quantity: null,
            unit: null,
          },
        ],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helper functions for number formatting and validation
  const formatNumber = (value: string | number) => {
    if (value === null || value === undefined || value === "") return "";
    const numStr = value.toString();
    if (numStr === "0") return "0";
    return numStr.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const unformatNumber = (value: string) => {
    return value.replace(/,/g, "");
  };

  const convertFullwidthToHalfwidth = (value: string) => {
    return value
      .replace(/[０-９]/g, (match) =>
        String.fromCharCode(match.charCodeAt(0) - 0xfee0)
      )
      .replace(/，/g, ",") // Fullwidth comma to halfwidth comma
      .replace(/．/g, "."); // Fullwidth period to halfwidth period
  };

  const convertFullwidthToHalfwidthText = (value: string) => {
    return value
      .replace(/[Ａ-Ｚａ-ｚ０-９]/g, (match) =>
        String.fromCharCode(match.charCodeAt(0) - 0xfee0)
      )
      .replace(/[！-～]/g, (match) =>
        String.fromCharCode(match.charCodeAt(0) - 0xfee0)
      )
      .replace(/\u3000/g, " "); // Fullwidth space to halfwidth space
  };

  const formatQuantity = (value: string | number) => {
    if (value === null || value === undefined || value === "") return "";
    const numStr = value.toString();
    if (numStr === "0") return "0";
    if (numStr.includes(".")) {
      const [integerPart, decimalPart] = numStr.split(".");
      const formattedInteger = integerPart.replace(
        /\B(?=(\d{3})+(?!\d))/g,
        ","
      );
      return `${formattedInteger}.${decimalPart}`;
    }
    return numStr.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const handleMoneyFieldChange = (
    value: string,
    fieldName: string,
    maxDigits = 8,
    maxValue = 99999999,
    shouldTrigger = false
  ) => {
    const converted = convertFullwidthToHalfwidth(value);
    const numeric = unformatNumber(converted).replace(/[^0-9]/g, "");
    const numValue = parseInt(numeric);
    if (
      numeric.length <= maxDigits &&
      (isNaN(numValue) || numValue <= maxValue)
    ) {
      props.setValue(fieldName as any, numeric);
      if (shouldTrigger) props.trigger(fieldName as any);
      handleValue();
    }
  };

  const handleQuantityFieldChange = (
    value: string,
    fieldName: string,
    maxDigits = 4,
    maxValue = 9999
  ) => {
    const converted = convertFullwidthToHalfwidth(value);
    const numeric = unformatNumber(converted).replace(/[^0-9.]/g, "");
    const parts = numeric.split(".");
    if (parts[0].length <= maxDigits) {
      const finalValue =
        parts.length > 1 ? `${parts[0]}.${parts[1].substring(0, 2)}` : parts[0];
      const numValue = parseFloat(finalValue);
      if (isNaN(numValue) || numValue <= maxValue) {
        props.setValue(fieldName as any, finalValue);
        handleValue();
      }
    }
  };

  const handleTextFieldChange = (
    value: string,
    fieldName: string,
    maxLength?: number
  ) => {
    const converted = convertFullwidthToHalfwidthText(value);
    let finalValue = converted;

    if (maxLength && converted.length > maxLength) {
      finalValue = converted.slice(0, maxLength);
    }

    props.setValue(fieldName as any, finalValue);
  };

  const enforceTextMaxLength = (
    value: string,
    fieldName: string,
    maxLength: number
  ) => {
    const converted = convertFullwidthToHalfwidthText(value);
    const finalValue =
      converted.length > maxLength ? converted.slice(0, maxLength) : converted;

    props.setValue(fieldName as any, finalValue);
    return finalValue;
  };

  const handleCompositionEnd = (
    e: React.CompositionEvent<HTMLDivElement | HTMLInputElement>,
    fieldName: string,
    maxLength: number
  ) => {
    const target = e.target as HTMLInputElement;
    const converted = convertFullwidthToHalfwidthText(target.value);

    if (converted.length > maxLength) {
      const truncated = converted.slice(0, maxLength);
      target.value = truncated;
      props.setValue(fieldName as any, truncated);
    } else {
      props.setValue(fieldName as any, converted);
    }
  };

  const handleInputWithComposition = (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | React.FormEvent<HTMLInputElement | HTMLDivElement>,
    fieldName: string,
    maxLength: number
  ) => {
    const target = e.target as HTMLInputElement;
    const value = target.value;

    const converted = convertFullwidthToHalfwidthText(value);
    if (converted.length > maxLength) {
      const truncated = converted.slice(0, maxLength);
      target.value = truncated;
      props.setValue(fieldName as any, truncated);
    } else {
      props.setValue(fieldName as any, converted);
    }
  };

  const handleProductNameComposition = (
    e: React.CompositionEvent<HTMLDivElement>,
    index: number,
    type: "start" | "end"
  ) => {
    const fieldName = `work_cost_items.${index}.product_name`;
    if (type != "start") {
      const target = e.target as HTMLInputElement;
      handleTextFieldChange(target.value, fieldName, 100);
    }
  };

  const handleUnitComposition = (
    e: React.CompositionEvent<HTMLDivElement>,
    index: number,
    type: "start" | "end"
  ) => {
    const fieldName = `work_cost_items.${index}.unit`;
    if (type != "start") {
      const target = e.target as HTMLInputElement;
      handleTextFieldChange(target.value, fieldName, 5);
    }
  };

  const handleProductNameKeyPress = (
    e: React.KeyboardEvent<HTMLDivElement>,
    index: number
  ) => {
    const fieldName = `work_cost_items.${index}.product_name`;

    if (
      e.ctrlKey ||
      e.metaKey ||
      e.key === "Backspace" ||
      e.key === "Delete" ||
      e.key === "ArrowLeft" ||
      e.key === "ArrowRight" ||
      e.key === "Tab"
    ) {
      return;
    }

    const target = e.target as HTMLInputElement;
    const currentValue = target.value || "";
    const converted = convertFullwidthToHalfwidthText(currentValue);

    if (converted.length >= 100) {
      e.preventDefault();
    }
  };

  const handleUnitKeyPress = (
    e: React.KeyboardEvent<HTMLDivElement>,
    index: number
  ) => {
    const fieldName = `work_cost_items.${index}.unit`;

    if (
      e.ctrlKey ||
      e.metaKey ||
      e.key === "Backspace" ||
      e.key === "Delete" ||
      e.key === "ArrowLeft" ||
      e.key === "ArrowRight" ||
      e.key === "Tab"
    ) {
      return;
    }

    const target = e.target as HTMLInputElement;
    const currentValue = target.value || "";
    const converted = convertFullwidthToHalfwidthText(currentValue);

    if (converted.length >= 5) {
      e.preventDefault();
    }
  };

  const handleKeyPress = (
    e: React.KeyboardEvent<HTMLDivElement | HTMLInputElement>,
    fieldName: string,
    maxLength?: number
  ) => {
    if (
      e.ctrlKey ||
      e.metaKey ||
      e.key === "Backspace" ||
      e.key === "Delete" ||
      e.key === "ArrowLeft" ||
      e.key === "ArrowRight" ||
      e.key === "ArrowUp" ||
      e.key === "ArrowDown" ||
      e.key === "Home" ||
      e.key === "End" ||
      e.key === "Tab"
    ) {
      return;
    }

    if (maxLength) {
      const target = e.target as HTMLInputElement;
      const currentValue = target.value || "";
      const converted = convertFullwidthToHalfwidthText(currentValue);

      if (converted.length >= maxLength) {
        e.preventDefault();
      }
    }
  };

  const handleNumberBlur = (fieldName: string, shouldTrigger = false) => {
    const currentValue = props.getValues(fieldName);
    if (currentValue !== undefined && currentValue !== null) {
      const numericValue = parseInt(currentValue.toString().replace(/,/g, ""));
      const normalizedValue = isNaN(numericValue)
        ? ""
        : numericValue.toString();
      props.setValue(fieldName as any, normalizedValue);
      if (shouldTrigger) props.trigger(fieldName as any);
      handleValue();
    }
  };

  const handleQuantityBlur = (fieldName: string) => {
    const currentValue = props.getValues(fieldName);
    if (currentValue !== undefined && currentValue !== null) {
      const numericValue = parseFloat(
        currentValue.toString().replace(/,/g, "")
      );
      const normalizedValue = isNaN(numericValue)
        ? ""
        : numericValue.toString();
      props.setValue(fieldName as any, normalizedValue);
      handleValue();
    }
  };

  const handleValue = () => {
    const workCostItems =
      props.getValues("work_cost_items")?.length < 0
        ? 0
        : props
            .getValues("work_cost_items")
            .reduce(
              (cur: any, value: any) =>
                cur +
                (Math.floor(
                  Number(value?.material_cost) * Number(value?.quantity)
                ) || 0),
              0
            );

    const businesTripFee = +props.getValues("busines_trip_fee") || 0;
    const workFee = +props.getValues("work_fee") || 0;
    const disposalFee = +props.getValues("disposal_fee") || 0;

    props.setValue("total_material_cost", workCostItems || "0");
    props.setValue(
      "total",
      businesTripFee + workFee + disposalFee + workCostItems || "0"
    );
  };

  const handleControlledTextInput = (
    value: string,
    fieldName: string,
    maxLength: number
  ) => {
    const converted = convertFullwidthToHalfwidthText(value);
    const finalValue =
      converted.length > maxLength ? converted.slice(0, maxLength) : converted;

    setTextFieldValues((prev) => ({ ...prev, [fieldName]: finalValue }));
    props.setValue(fieldName as any, finalValue);

    return finalValue;
  };

  const handleKeyPressBlock = (
    e: React.KeyboardEvent<HTMLInputElement | HTMLDivElement>,
    fieldName: string,
    maxLength: number
  ) => {
    const currentValue = getControlledValue(fieldName);
    const converted = convertFullwidthToHalfwidthText(currentValue);

    if (converted.length >= maxLength) {
      if (
        e.ctrlKey ||
        e.metaKey ||
        e.key === "Backspace" ||
        e.key === "Delete" ||
        e.key === "ArrowLeft" ||
        e.key === "ArrowRight" ||
        e.key === "ArrowUp" ||
        e.key === "ArrowDown" ||
        e.key === "Home" ||
        e.key === "End" ||
        e.key === "Tab" ||
        e.key === "Escape" ||
        e.key === "Enter"
      ) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();
    }
  };

  const handleStrictInput = (
    e:
      | React.FormEvent<HTMLInputElement | HTMLDivElement>
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | React.CompositionEvent<HTMLDivElement>,
    fieldName: string,
    maxLength: number
  ) => {
    const target = e.target as HTMLInputElement;
    const rawValue = target.value;

    if (isComposing[fieldName] && e.type !== "compositionend") {
      setTextFieldValues((prev) => ({ ...prev, [fieldName]: rawValue }));
      return;
    }

    const converted = convertFullwidthToHalfwidthText(rawValue);

    const finalValue =
      converted.length > maxLength
        ? converted.slice(0, maxLength) // Keep first maxLength characters
        : converted;

    setTextFieldValues((prev) => ({ ...prev, [fieldName]: finalValue }));
    props.setValue(fieldName as any, finalValue);

    if (target.value !== finalValue) {
      target.value = finalValue;
      setTimeout(() => {
        target.setSelectionRange(finalValue.length, finalValue.length);
      }, 0);
    }
  };

  const getControlledValue = (fieldName: string) => {
    const formValue = props.getValues(fieldName);
    const stateValue = textFieldValues[fieldName];

    return stateValue !== undefined ? stateValue : formValue || "";
  };

  const handleCompositionStart = (fieldName: string) => {
    setIsComposing((prev) => ({ ...prev, [fieldName]: true }));
  };

  const handleCompositionEndFixed = (
    e: React.CompositionEvent<HTMLDivElement | HTMLInputElement>,
    fieldName: string,
    maxLength: number
  ) => {
    setIsComposing((prev) => ({ ...prev, [fieldName]: false }));
    handleStrictInput(e, fieldName, maxLength);
  };

  return {
    ...props,
    fields,
    openModalDelete,
    setOpenModalDelete,
    remove,
    handleValue,
    append,
    removeIdx,
    setRemoveIdx,
    formatNumber,
    formatQuantity,
    handleMoneyFieldChange,
    handleQuantityFieldChange,
    handleTextFieldChange,
    enforceTextMaxLength,
    handleInputWithComposition,
    handleCompositionEnd,
    handleKeyPress,
    handleNumberBlur,
    handleQuantityBlur,
    handleProductNameComposition,
    handleUnitComposition,
    handleProductNameKeyPress,
    handleUnitKeyPress,
    handleControlledTextInput,
    getControlledValue,
    handleKeyPressBlock,
    handleStrictInput,
    handleCompositionStart,
    handleCompositionEndFixed,
  };
};

export type Props = ReturnType<typeof useWorkCost>;

export default useWorkCost;
